import type { BotStrategyConfiguration } from 'api/elplan/elplan.types'
import { useBotStrategySettingQuery } from 'api/elplan/intradayTrading.api'
import type { ReactNode } from 'react'
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { HourlyDataTable } from 'ui/components/HourlyDataTable/HourlyDataTable'
import { getCellDisplay } from 'ui/components/HourlyDataTable/HourlyDataTable.cellEdit.helper'
import hourlyStyles from 'ui/components/HourlyDataTable/HourlyDataTable.module.less'
import type { RowValues, TableCellEditRule } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import { HourlyInputCell } from 'ui/components/HourlyDataTable/HourlyInputCell'
import { useTableCellClipboard } from 'ui/components/HourlyDataTable/hooks/useTableCellClipboard'
import { useTableCellEdit } from 'ui/components/HourlyDataTable/hooks/useTableCellEdit'
import { useTableCellNavigation } from 'ui/components/HourlyDataTable/hooks/useTableCellNavigation'
import { useTableCellSelection } from 'ui/components/HourlyDataTable/hooks/useTableCellSelection'
import { getPrefillWindow, PREFILL_WINDOW_HOURS } from '../../helpers/intradayBidSubmissions.helper'
import styles from '../../IntradayTradingView.module.less'
import { getBidHourRangeLabels, getBidRowDateLabel } from './helpers/BidInputTable.time'
import { mapBotStrategyResponseToRowValues } from './helpers/BotStrategyTable.helpers'
import { makeStrategyCellKey, makeStrategyTableAdapter } from './tableCellAdapters/strategyTableAdapter'

const STRATEGY_VALUE_RULE: TableCellEditRule = { kind: 'number', admitsSign: true }

type BotStrategyTableProps = {
  series: BotStrategyConfiguration['series']
  bidBaseStartTime: Date
  systemId?: number
  onValuesChange?: (values: Record<string, string>) => void
  rowCount: number
  onAddRow: () => void
  onRemoveLastRow: () => void
}

export const BotStrategyTable = memo(function BotStrategyTable(props: BotStrategyTableProps): ReactNode {
  if (props.series.length === 0) {
    return null
  }
  return <BotStrategyTableInner {...props} />
})

function useBotStrategyTableBehavior(
  series: BotStrategyConfiguration['series'],
  bidBaseStartTime: Date,
  systemId: number | undefined,
  onValuesChange: ((values: Record<string, string>) => void) | undefined,
  rowCount: number
) {
  const { startTime, endTime } = getPrefillWindow(bidBaseStartTime)
  const { data: prefillResponse } = useBotStrategySettingQuery(systemId, startTime, endTime)

  const [valuesByHour, setValuesByHour] = useState<RowValues[]>(() => {
    if (prefillResponse) {
      return mapBotStrategyResponseToRowValues(prefillResponse, bidBaseStartTime, rowCount)
    }
    return Array.from({ length: rowCount }, () => ({}))
  })
  const prefillRowsRef = useRef<RowValues[] | undefined>(undefined)
  const valuesByHourRef = useRef<RowValues[]>([])
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  useEffect(() => {
    valuesByHourRef.current = valuesByHour
  }, [valuesByHour])

  // Apply prefill data when the query resolves after initial render
  // biome-ignore lint/correctness/useExhaustiveDependencies: bidBaseStartTime changes always coincide with prefillResponse changing (it's part of the query key), so listing it separately would cause a redundant re-run without any benefit.
  useEffect(() => {
    if (!prefillResponse) return
    setValuesByHour((previous) => {
      // Use previous.length so we cover all currently visible rows regardless of rowCount
      const prefill = mapBotStrategyResponseToRowValues(prefillResponse, bidBaseStartTime, previous.length)
      return previous.map((row, index) => {
        const prefillRow = prefill[index] ?? {}
        const merged: RowValues = { ...row }
        for (const [key, value] of Object.entries(prefillRow)) {
          if (!(key in merged)) {
            merged[key] = value
          }
        }
        return merged
      })
    })
  }, [prefillResponse])

  useEffect(() => {
    if (!onValuesChange) return
    const flat: Record<string, string> = {}
    for (const hourValues of valuesByHour) {
      Object.assign(flat, hourValues)
    }
    onValuesChange(flat)
  }, [valuesByHour, onValuesChange])

  // Keep prefillRowsRef current so the row-expansion effect can seed new rows
  // from pre-fetched data without a stale-closure problem.
  useEffect(() => {
    prefillRowsRef.current = prefillResponse
      ? mapBotStrategyResponseToRowValues(prefillResponse, bidBaseStartTime, PREFILL_WINDOW_HOURS)
      : undefined
  })

  // Sync valuesByHour array length to rowCount, seeding new rows from prefill
  useEffect(() => {
    setValuesByHour((previous) => {
      if (previous.length === rowCount) return previous
      if (previous.length < rowCount) {
        const currentPrefill = prefillRowsRef.current
        return [
          ...previous,
          ...Array.from({ length: rowCount - previous.length }, (_, offset) => {
            const hourIndex = previous.length + offset
            return currentPrefill?.[hourIndex] ?? {}
          }),
        ]
      }
      return previous.slice(0, rowCount)
    })
  }, [rowCount])

  const adapter = makeStrategyTableAdapter(series, rowCount)

  const adapterRef = useRef(adapter)
  useLayoutEffect(() => {
    adapterRef.current = adapter
  })

  const {
    selectedCellKeys,
    selectedCellKeysRef,
    selectedCellKeysForClipboardRef,
    anchorCellKeyForClipboardRef,
    bulkReplaceOnNextInputRef,
    setAnchorCellKey,
    replaceSelection,
    clearSelection,
    handleCellMouseDown,
    handleCellMouseOver,
    getEditTargets,
    takeReplaceOnType,
  } = useTableCellSelection({ adapter, tableContainerRef })

  const clearValuesForSelection = (cellKeys: Set<string>) => {
    if (cellKeys.size === 0) return
    setValuesByHour((previous) => {
      const next = [...previous]
      cellKeys.forEach((cellKey) => {
        const coord = adapterRef.current.getCoordinate(cellKey)
        if (!coord || coord.rowIndex >= next.length) return
        next[coord.rowIndex] = { ...(next[coord.rowIndex] ?? {}), [cellKey]: '' }
      })
      return next
    })
  }

  const { handleCellKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
    adapter,
    clearValuesForSelection,
    clearSelection,
    replaceSelection,
    setAnchorCellKey,
    inputRefs,
    bulkReplaceOnNextInputRef,
    selectedCellKeysRef,
  })

  const { handleCellValueChange, handleCellKeyDown } = useTableCellEdit({
    adapter,
    inputRefs,
    valuesByRow: valuesByHour,
    setValuesByRow: setValuesByHour,
    getCellRule: () => STRATEGY_VALUE_RULE,
    getEditTargets,
    takeReplaceOnType,
    handleNavigationKeyDown,
  })

  useTableCellClipboard({
    tableContainerRef,
    adapter,
    anchorCellKeyForClipboardRef,
    selectedCellKeysForClipboardRef,
    valuesByRowRef: valuesByHourRef,
    setValuesByRow: setValuesByHour,
  })

  const registerInputRef = (cellKey: string, element: HTMLInputElement | null) => {
    if (element === null) {
      delete inputRefs.current[cellKey]
      return
    }
    inputRefs.current[cellKey] = element
  }

  return {
    valuesByHour,
    selectedCellKeys,
    tableContainerRef,
    handleCellValueChange,
    handleCellKeyDown,
    handleCellMouseDown,
    handleCellMouseOver,
    registerInputRef,
  }
}

function BotStrategyTableInner({
  series,
  bidBaseStartTime,
  systemId,
  onValuesChange,
  rowCount,
  onAddRow: handleAddRow,
  onRemoveLastRow: handleRemoveLastRow,
}: BotStrategyTableProps): ReactNode {
  const { t } = useTranslation()
  const {
    valuesByHour,
    selectedCellKeys,
    tableContainerRef,
    handleCellValueChange,
    handleCellKeyDown,
    handleCellMouseDown,
    handleCellMouseOver,
    registerInputRef,
  } = useBotStrategyTableBehavior(series, bidBaseStartTime, systemId, onValuesChange, rowCount)
  const hourLabels = getBidHourRangeLabels(bidBaseStartTime, rowCount)

  return (
    <div className={styles.IntradayTradingView_BotStrategyTableContainer}>
      <div className={styles.IntradayTradingView_BidTableLayout}>
        <HourlyDataTable
          isCompact
          rowCount={rowCount}
          tableContainerRef={tableContainerRef}
          getRowLabel={(i) => hourLabels[i]}
          getDateLabel={(i) => getBidRowDateLabel(bidBaseStartTime, i)}
          totalColumnCount={1 + series.length}
          columnHeaders={
            <>
              {series.map((s) => (
                <th key={s.id} className={hourlyStyles.HourlyDataTable_BidTableGroupColumnHeader}>
                  {s.display_name}
                </th>
              ))}
            </>
          }
          renderCellsForRow={(hourIndex) => (
            <>
              {series.map((s) => {
                const cellKey = makeStrategyCellKey(hourIndex, s.id)
                return (
                  <HourlyInputCell
                    key={s.id}
                    cellKey={cellKey}
                    value={getCellDisplay(valuesByHour[hourIndex]?.[cellKey] ?? '', STRATEGY_VALUE_RULE)}
                    isSelected={selectedCellKeys.has(cellKey)}
                    isInvalid={false}
                    isGroupStart={true}
                    isGroupEnd={true}
                    ariaLabel={`${s.display_name} ${hourLabels[hourIndex]}`}
                    onValueChange={(raw) => handleCellValueChange(cellKey, raw)}
                    onFocus={() => {}}
                    onBlur={() => {}}
                    onKeyDown={(ev) => handleCellKeyDown(cellKey, ev)}
                    onMouseDown={(ev) => handleCellMouseDown(cellKey, ev)}
                    onMouseOver={() => handleCellMouseOver(cellKey)}
                    registerRef={(el) => registerInputRef(cellKey, el)}
                  />
                )
              })}
            </>
          )}
        />
        {rowCount > 0 ? (
          <div className={styles.IntradayTradingView_BidTableAction}>
            <NewButton
              iconButton
              variant="delete"
              iconSize="small"
              onClick={handleRemoveLastRow}
              tooltip={{ title: t('Remove last row'), placement: 'top' }}
            />
          </div>
        ) : null}
      </div>
      <div className={styles.IntradayTradingView_BidTableActionRow}>
        <NewButton
          variant="outlined"
          label={t('Add more')}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            height: 'auto',
            lineHeight: 1.25,
            borderRadius: 0,
          }}
          onClick={handleAddRow}
        />
      </div>
    </div>
  )
}
